import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Edit, Save, X, Calendar, Package, Truck, FileText, Clipboard, Ship, Upload, DollarSign, Plane, CheckCircle, AlertCircle, ExternalLink, Loader2 } from 'lucide-react';
import { cn } from '../lib/utils';
import ShipmentDocuments from './ShipmentDocuments';
import * as utils from '../lib/utils.js';
import { useNotification } from '../lib/NotificationProvider';
import { useUserData } from '../contexts/UserDataContext';
import { useQueryClient } from '@tanstack/react-query';
import { useLineItems } from '../hooks/useLineItems';

const FORWARDER_OPTIONS = ['Alba Wheels Up', 'Fashion Forward'];

const ShipmentForm = ({ isCreate = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { markDataChanged } = useNotification();
  const { userData } = useUserData();
  // Subscribe to the bulk line items cache populated by MainContent
  // (App.js). When navigating here from Dashboard or LineItemForm, the
  // data is already cached — no second fetch, no rerender flicker.
  const {
    data: bulkLineItems = [],
    isLoading: loadingLineItems,
    error: bulkLineItemsError,
  } = useLineItems('', userData);
  const lineItemsError = bulkLineItemsError
    ? 'Failed to load line items for this shipment.'
    : null;
  const [shipment, setShipment] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [showUploadPopup, setShowUploadPopup] = useState(false);
  const [isEditing, setIsEditing] = useState(isCreate);
  const [formData, setFormData] = useState({
    Shipment: '',
    'Forwarder Name': '',
    'Order Ref': '',
    'Consignee Name': '',
    'Consignor Name': '',
    'Origin': '',
    'Dest.': '',
    'House': '',
    'Master': '',
    'Job Notes': '',
    'Mode': '',
    'Container #': '',
    'Vessel': '',
    'Arrival Vessel': '',
    'Weight': '',
    'WTUQ': '',
    'Volume': '',
    'VOLUQ': '',
    'Outer': '',
    'PACKTYPE': '',
    'UQ - Outer': '',
    'OBL Received Date': '',
    'ETD': '',
    'Origin ETD': '',
    'ATD': '',
    'ETA': '',
    'Dest. ETA': '',
    'ATA': '',
    'Est Delivery': '',
    'Act Delivery': '',
    'Charge': '',
    'UQ-Charge': '',
    'Trans': '',
    'Cntr': ''
  });
  const [loading, setLoading] = useState(!isCreate);
  const [activeTab, setActiveTab] = useState('dates');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [newLineItems, setNewLineItems] = useState('');
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const queryClientHook = useQueryClient(); // Get the query client instance

  const consigneeOptions = ["MARKS USA", "NAPCO DR SA", "NAPCO SECURITY TECHNOLOGIES"];
  const originPortOptions = ["CNBJS", "CNCAN", "CNCGO", "CNJAX", "CNJMN", "CNNBO", "CNNGB", "CNNJI", "CNPEK", "CNPNS", "CNPVG", "CNQDG", "CNQIN", "CNSHA", "CNSHE", "CNSHG", "CNSHK", "CNSNZ", "CNSZP", "CNSZX", "CNTAO", "CNTAZ", "CNXAM", "CNXGA", "CNXIA", "CNXIP", "CNXMG", "CNXMN", "CNYTN", "CNZSN", "DEFRA", "DESTR", "HKHKG", "HKKWN", "INJGA", "INMUM", "JPHND", "JPOSA", "JPYOK", "KRICN", "KRPUS", "KRTAE", "MYPEN", "Shanghai Railway Station", "Shenzhen Baoan International Apt", "Shenzhen Pt", "THBKK", "TWKEL", "TWKHH", "TWTPE", "USAYZ", "USHOU", "USJFK", "USLAX", "USMIA", "USNBL", "VNSGN"];
  const destinationPortOptions = ["DOHAI", "DOSDQ", "USNYC", "USJFK", "DOCAU", "USMIA", "USLAX", "USPEB", "DOSCR", "CNNBO", "CNCAN", "USBOS", "CNNGB"];
  const uqChargeOptions = ["M3", "KG"];
  const uqOuterOptions = ["CTN", "PLT", "PKG", "PC", "PCE", "UNT", "SKD", "CAS", "BOX", "CRT"];
  const uqVolumeOptions = ["M3", "KG", "CF"];
  const uqWeightOptions = ["KG", "LB", "KT", "LT"];
  const transOptions = ["SEA", "AIR", "COU", "ROA", "BCN", "LCL", "LSE", "RAI", "FSA", "UPS"];
  const modeOptions = ["LCL", "LSE", "BCN", "FCL", "AIR", "SEA", "COU", "ROA", "RAI", "FSA"];

  const tabs = [
    { id: 'dates', label: 'Dates', icon: <Calendar size={16} className="mr-1" /> },
    { id: 'container', label: 'Container', icon: <Ship size={16} className="mr-1" /> },
    ...(isCreate ? [] : [
      { id: 'parts', label: 'Line Items & Parts', icon: <Truck size={16} className="mr-1" /> },
      { id: 'documents', label: 'Documents', icon: <FileText size={16} className="mr-1" /> }
    ])
  ];

  useEffect(() => {
    if (isCreate) {
      if (userData && userData.role === 'Forwarder' && userData.company_name) {
        setFormData(prevData => ({
          ...prevData,
          'Forwarder Name': userData.company_name
        }));
      }
    }
  }, [isCreate, userData]);

  useEffect(() => {
    if (!isCreate && id) {
      const fetchShipment = async () => {
        try {
          const query = `?${new URLSearchParams({ shipment: id }).toString()}`;
          const [shipmentRes, documentRes] = await Promise.all([
            utils.getShipments(query),
            utils.getDocuments(query)
          ]);
          console.log('shipmentRes:', shipmentRes);
          if (shipmentRes.data.shipments && shipmentRes.data.shipments.length > 0) {
            const shipmentData = shipmentRes.data.shipments[0];
            setShipment(shipmentData);
            
            if (userData && userData.role === 'Forwarder' && userData.company_name) {
              setFormData({
                ...shipmentData,
                'Forwarder Name': userData.company_name
              });
            } else {
              setFormData(shipmentData);
            }
          }
          if (documentRes.data.documents && documentRes.data.documents.length > 0) {
            setDocuments(documentRes.data.documents);
          }
        } catch (error) {
          console.error('Error fetching shipment:', error);
        } finally {
          setLoading(false);
        }
      };
      fetchShipment();
    }
  }, [id, isCreate, userData]);

  // Derive this shipment's line items from the bulk cache. The bulk
  // endpoint already filters hidden items server-side, so this list
  // matches what the targeted ?shipment= query used to return.
  const lineItems = useMemo(() => {
    if (isCreate || !id) return [];
    const idKey = id.toString().trim();
    return bulkLineItems.filter(
      li => (li.related_shipment ?? '').toString().trim() === idKey
    );
  }, [bulkLineItems, id, isCreate]);

  // POs reachable from this shipment — i.e., not hidden/deleted server-side.
  // Hidden line items are filtered out by the API, so a PO present in
  // `Order Ref` but absent from `lineItems` cannot be navigated to.
  const reachablePoSet = useMemo(
    () => new Set(lineItems.map(li => (li.title ?? '').trim()).filter(Boolean)),
    [lineItems]
  );

  const isPoUnreachable = (po) => {
    if (loadingLineItems || lineItemsError) return false;
    return !reachablePoSet.has((po ?? '').trim());
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleNewLineItemsChange = (e) => {
    setNewLineItems(e.target.value);
  };

  // Invalidate React Query so the next mount of the dashboard / forms refetches
  // fresh data from the backend (whose in-memory cache was patched on update).
  const invalidateLists = () => {
    queryClientHook.invalidateQueries({ queryKey: ['shipments'] });
    queryClientHook.invalidateQueries({ queryKey: ['lineItems'] });
  };

  const handleSave = async () => {
    setError(null);
    setIsSaving(true);

    try {
      const shipmentData = { ...formData };

      const dateFields = [
        'OBL Received Date', 'ETD', 'Origin ETD', 'ATD', 
        'ETA', 'Dest. ETA', 'ATA', 'Est Delivery', 'Act Delivery'
      ];
      
      dateFields.forEach(field => {
        if (shipmentData[field]) {
          shipmentData[field] = new Date(shipmentData[field]).toISOString();
        }
      });

      // Handle Line Items (Order Ref)
      if (newLineItems) {
        const existingItems = shipmentData['Order Ref'] ? shipmentData['Order Ref'] : '';
        const cleanedNewItems = newLineItems.replace(/,\s*$/, '');
        
        if (existingItems) {
          shipmentData['Order Ref'] = existingItems + ',' + cleanedNewItems;
        } else {
          shipmentData['Order Ref'] = cleanedNewItems;
        }
      }

      if (shipmentData['Order Ref']) {
        const cleanedOrderRef = shipmentData['Order Ref'].replace(/,\s*$/, '');
        shipmentData['Order Ref'] = cleanedOrderRef
            .split(',')
            .map(po => po.replace(/\s+/g, ''))  // Remove ALL whitespace
            .filter(po => po);
      }

      if (isCreate) {
        // Create the shipment
        const response = await utils.createShipment([shipmentData]);
        setSuccessMessage('Shipment created successfully!');
        setShowSuccessPopup(true);
        markDataChanged(); // Mark data as changed

        invalidateLists();

        // Navigate after a short delay to allow the user to see the success message
        setTimeout(() => {
          // Hard refresh the page by redirecting to the dashboard
          window.location.href = '/';
        }, 3000);
      } else {
        // Update the shipment in the backend
        console.log('Updating shipment with ID:', shipment);
        const response = await utils.updateShipment(shipmentData, shipment.Shipment);
        console.log('Update response:', response);
        
        // Update the local state
        setShipment({
          ...shipment,
          ...formData
        });
        setIsEditing(false);
        markDataChanged(); // Mark data as changed
        setSuccessMessage('Shipment updated successfully!');
        setShowSuccessPopup(true);
        
        invalidateLists();
        
        // Navigate after a short delay with a hard refresh to update localStorage
        setTimeout(() => {
          window.location.href = '/';
        }, 3000);
      }
    } catch (err) {
      console.error('Error saving shipment:', err);
      const errorMessage = err.response?.data?.message
        || (isCreate ? 'Failed to create shipment. Please try again.' : 'Failed to update shipment. Please try again.');
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const toggleEdit = () => {
    if (isEditing && !isCreate) {
      setFormData(shipment);
    }
    setIsEditing(!isEditing);
  };

  const renderOrderRefs = (orderRefs) => {
    const poNumbers = Array.isArray(orderRefs) ? orderRefs : orderRefs.split(',')
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-600 mb-1 flex items-center">
          <FileText size={16} className="mr-1.5 text-gray-400" />
          Line Items
        </label>
        <div className="p-2.5 border rounded-md bg-gray-50 border-gray-200 text-gray-800 flex flex-wrap gap-2">
          {poNumbers.map((po, index) => (
            isPoUnreachable(po) ? (
              <span
                key={index}
                title="This line item is hidden and cannot be viewed."
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed opacity-60"
              >
                {po}
              </span>
            ) : (
              <button
                key={index}
                onClick={() => navigate(
                  `/line-item?${new URLSearchParams({ po, shipment: shipment?.Shipment || '' })}`,
                  {
                    state: {
                      fromShipment: true,
                      shipmentId: shipment?.Shipment,
                    },
                  }
                )}
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-800 hover:bg-accent-200 transition-colors border border-accent-200 shadow-sm"
              >
                {po}
              </button>
            )
          ))}
        </div>
      </div>
    );
  };

  const renderField = (label, value, fieldName, icon = null, editable = true) => {
    let options = [];
    // Assign the correct options array based on the field name
    if (fieldName === 'Consignee Name') options = consigneeOptions;
    else if (fieldName === 'Origin') options = originPortOptions;
    else if (fieldName === 'Dest.') options = destinationPortOptions;
    else if (fieldName === 'UQ-Charge') options = uqChargeOptions;
    else if (fieldName === 'UQ - Outer' || fieldName === 'UQ-Outer') options = uqOuterOptions;
    else if (fieldName === 'VOLUQ') options = uqVolumeOptions;
    else if (fieldName === 'WTUQ') options = uqWeightOptions;
    else if (fieldName === 'Trans') options = transOptions;
    else if (fieldName === 'Mode') options = modeOptions;
    
    // Disable editing the Shipment field when editing an existing shipment
    const isEditableField = (editable && isEditing) || isCreate;
    const isShipmentField = fieldName === 'Shipment';
    const isDisabled = isShipmentField && !isCreate;
    
    if (fieldName === 'Forwarder Name' && (isEditableField || isCreate)) {
      if (userData && userData.role === 'Forwarder' && userData.company_name) {
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600 mb-1 flex items-center">
              {icon && <span className="mr-1.5 text-gray-400">{icon}</span>}
              {label}
            </label>
            <input
              type="text"
              name={fieldName}
              value={userData.company_name || ''}
              disabled={true}
              className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-accent-500 bg-gray-100 cursor-not-allowed"
              placeholder={`Enter ${label}`}
            />
            <p className="text-xs text-gray-500 mt-1">Fixed to your company name as a Forwarder</p>
          </div>
        );
      }
      
      return (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-600 mb-1 flex items-center">
            {icon && <span className="mr-1.5 text-gray-400">{icon}</span>}
            {label}
          </label>
          <input
            type="text"
            name={fieldName}
            value={formData[fieldName] || ''}
            onChange={handleInputChange}
            className="w-full p-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-accent-500 bg-gray-50"
            placeholder={`Enter ${label}`}
          />
        </div>
      );
    }

    if (fieldName === 'Order Ref' && (isEditableField || isCreate)) {
      return (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-600 mb-1 flex items-center">
            {icon && <span className="mr-1.5 text-gray-400">{icon}</span>}
            {label}
          </label>
          {!isCreate && formData[fieldName] && (
            <div className="mb-2 p-2.5 border rounded-md bg-gray-50 border-gray-200 text-gray-800 flex flex-wrap gap-2">
              {(Array.isArray(formData[fieldName]) ? formData[fieldName] : formData[fieldName].split(','))
                .map((po, index) => (
                  isPoUnreachable(po) ? (
                    <span
                      key={index}
                      title="This line item is hidden and cannot be viewed."
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-400 border border-gray-200 cursor-not-allowed opacity-60"
                    >
                      {po}
                    </span>
                  ) : (
                    <button
                      key={index}
                      onClick={() => navigate(
                        `/line-item?${new URLSearchParams({ po, shipment: shipment?.Shipment || '' })}`,
                        {
                          state: {
                            fromShipment: true,
                            shipmentId: shipment?.Shipment,
                          },
                        }
                      )}
                      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-800 hover:bg-accent-200 transition-colors border border-accent-200 shadow-sm"
                    >
                      {po}
                    </button>
                  )
                ))}
            </div>
          )}
          <input
            type="text"
            name="newLineItems"
            value={newLineItems}
            onChange={handleNewLineItemsChange}
            className={cn(
              "w-full p-2.5 border rounded-md focus:outline-none focus:ring-1 bg-white",
              "border-gray-300 focus:ring-accent-500 focus:border-accent-500",
              "hover:border-gray-400 transition-colors duration-200",
              "placeholder:text-gray-400"
            )}
            placeholder={isCreate ? "Enter comma-separated line items" : "Add new line items (comma-separated)"}
          />
          <div className="mt-1.5 flex items-center space-x-1.5">
            <span className="inline-block w-1 h-1 rounded-full bg-accent-400"></span>
            <p className="text-xs text-gray-500">
              {isCreate 
                ? "Separate multiple line items with commas: xyz123, abc456, def789" 
                : "Add new line items separated by commas. Existing items cannot be modified here."}
            </p>
          </div>
        </div>
      );
    }

    if (fieldName === 'Consignee Name' || fieldName === 'Origin' || fieldName === 'Dest.' || fieldName === 'UQ-Charge' || fieldName === 'UQ-Outer' || fieldName === 'VOLUQ' || fieldName === 'WTUQ' || fieldName === 'Trans' || fieldName === 'Mode') {
      if (isEditableField || isCreate) {
        return (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-600 mb-1 flex items-center">
              {icon && <span className="mr-1.5 text-gray-400">{icon}</span>}
              {label}
            </label>
            <div className="relative">
              <select
                name={fieldName}
                value={formData[fieldName] || ''}
                onChange={handleInputChange}
                className={cn(
                  "w-full p-2.5 border rounded-md focus:outline-none focus:ring-1 bg-white appearance-none pr-10",
                  "border-gray-300 focus:ring-accent-500 focus:border-accent-500",
                  "hover:border-gray-400 transition-colors duration-200",
                  !formData[fieldName] && "text-gray-400 placeholder-gray-400"
                )}
              >
                <option value="" className="text-gray-400">Select {label}</option>
                {options.map(option => (
                  <option key={option} value={option} className="text-gray-600">
                    {option}
                  </option>
                ))}
              </select>
              <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>
        );
      }
    }

    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-600 mb-1 flex items-center">
          {icon && <span className="mr-1.5 text-gray-400">{icon}</span>}
          {label}
          {isShipmentField && !isCreate && (
            <span className="ml-2 text-xs text-red-500">(cannot be modified)</span>
          )}
        </label>
        {isEditableField ? (
          <input
            type="text"
            name={fieldName}
            value={formData[fieldName] || ''}
            onChange={handleInputChange}
            disabled={isDisabled}
            className={cn(
              "w-full p-2.5 border rounded-md focus:outline-none focus:ring-1",
              isDisabled 
                ? "bg-gray-100 border-gray-300 text-gray-500 cursor-not-allowed" 
                : "bg-gray-50 border-gray-300 focus:ring-accent-500"
            )}
            placeholder={`Enter ${label}`}
          />
        ) : (
          <div className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-md text-gray-600">
            {value || '-'}
          </div>
        )}
        {isShipmentField && !isCreate && isEditing && (
          <p className="text-xs text-red-500 mt-1">The shipment ID cannot be modified after creation</p>
        )}
      </div>
    );
  };

  const renderDateField = (label, value, fieldName, icon = null) => {
    const isEditableField = (isEditing || isCreate);
    
    return (
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-600 mb-1 flex items-center">
          {icon && <span className="mr-1.5 text-gray-400">{icon}</span>}
          {label}
        </label>
        <div className="rounded-md overflow-hidden">
          {isEditableField ? (
            <input
              type="date"
              name={fieldName}
              value={formData[fieldName] || ''}
              onChange={handleInputChange}
              className="w-full p-2.5 border border-gray-300 rounded-md bg-white"
            />
          ) : (
            <div className="p-2.5 border rounded-md flex items-center bg-gray-50 border-gray-200 text-gray-800">
              <span>{value || '-'}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderLineItemsTable = () => {
    console.log("Line Items Debug:", {
        lineItems,
        firstItem: lineItems[0],
        partNumbers: lineItems[0]?.part_numbers,
        loadingLineItems,
        lineItemsError
    });
    if (loadingLineItems) {
      return (
        <div className="animate-pulse space-y-4">
          <div className="h-10 bg-gray-200 rounded w-full"></div>
          <div className="h-10 bg-gray-200 rounded w-full"></div>
          <div className="h-10 bg-gray-200 rounded w-full"></div>
        </div>
      );
    }

    if (lineItemsError) {
      return (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center">
            <AlertCircle size={20} className="text-red-500 mr-2" />
            <p className="text-red-700">{lineItemsError}</p>
          </div>
        </div>
      );
    }

    if (lineItems.length === 0) {
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-md p-8 text-center">
          <Truck size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500">No line items associated with this shipment</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                PO Number
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Forwarder
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total Quantity
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Parts
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {lineItems.map((item, index) => (
              <tr key={index} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {item.title || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {item.forwarder_name || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {Array.isArray(item.part_numbers) && item.part_numbers.length > 0
                    ? item.part_numbers
                        .reduce((sum, part) => sum + (typeof part === 'object' && part !== null ? (parseInt(part.qty) || 0) : 0), 0)
                        .toLocaleString()
                    : '—'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500">
                  {Array.isArray(item.part_numbers) ? (
                    <div className="flex flex-wrap gap-1.5">
                      {item.part_numbers.map((part, partIndex) => (
                        <span 
                          key={partIndex} 
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                        >
                          {typeof part === 'object' ? part.part_number : part}
                        </span>
                      ))}
                    </div>
                  ) : '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <button
                    onClick={() => navigate(
                      `/line-item?${new URLSearchParams({
                        po: item.title,
                        shipment: item.related_shipment || shipment?.Shipment || '',
                      })}`,
                      {
                        state: {
                          fromShipment: true,
                          shipmentId: shipment?.Shipment,
                        },
                      }
                    )}
                    className="inline-flex items-center px-3 py-1.5 border border-accent-300 text-xs font-medium rounded-md text-accent-700 bg-white hover:bg-accent-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500"
                  >
                    <ExternalLink size={14} className="mr-1.5" />
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="flex justify-between items-center mb-4">
            <div className="h-6 bg-gray-200 rounded w-44" />
            <div className="h-8 bg-gray-200 rounded w-24" />
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="h-10 bg-gray-200 rounded" />
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/2" />
                <div className="h-10 bg-gray-200 rounded" />
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="h-10 bg-gray-200 rounded" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-2/3" />
                <div className="h-10 bg-gray-200 rounded" />
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-2/3" />
                <div className="h-10 bg-gray-200 rounded" />
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="h-10 bg-gray-200 rounded" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/4" />
                <div className="h-10 bg-gray-200 rounded" />
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/4" />
                <div className="h-10 bg-gray-200 rounded" />
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/4" />
                <div className="h-10 bg-gray-200 rounded" />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/4" />
                <div className="h-10 bg-gray-200 rounded" />
              </div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/4" />
                <div className="h-10 bg-gray-200 rounded" />
              </div>
            </div>
            <div className="border-b border-gray-200" />
            <div className="space-x-4">
              <div className="h-8 bg-gray-200 rounded w-24" />
              <div className="h-8 bg-gray-200 rounded w-24" />
              <div className="h-8 bg-gray-200 rounded w-24" />
              <div className="h-8 bg-gray-200 rounded w-24" />
              <div className="h-8 bg-gray-200 rounded w-24" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!shipment && !isCreate) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500 mb-4">Shipment not found</p>
        <button
          onClick={() => navigate(-1)}
          className="text-accent-600 hover:text-accent-800 font-medium flex items-center justify-center mx-auto"
        >
          <ArrowLeft size={16} className="mr-1" />
          Go Back
        </button>
      </div>
    );
  }

  const canEditShipment = () => {
    if (!userData) return false;
    return userData.role !== "Employee";
  };

  const isForwarder = () => {
    if (!userData) return false;
    return userData.role === "Forwarder";
  };

  return (
    <div className="p-6">
      {showSuccessPopup && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full mx-4 transform transition-all animate-fade-in-up">
            <div className="flex items-center justify-center mb-4">
              <CheckCircle size={48} className="text-accent-500 mr-2" />
            </div>
            <h3 className="text-xl font-semibold text-center text-gray-800 mb-2">{successMessage}</h3>
            <p className="text-gray-600 text-center mb-4">You will be redirected to the dashboard shortly.</p>
            <div className="flex justify-center">
              <button
                onClick={() => {
                  setShowSuccessPopup(false);
                  window.location.href = '/';
                }}
                className="px-4 py-2 bg-accent-600 text-white rounded-md hover:bg-accent-700"
              >
                Go to Dashboard Now
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="mb-6 flex justify-between items-center">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => {
              if (location.state?.lineItemPo && location.state?.lineItemShipment) {
                navigate(
                  `/line-item?${new URLSearchParams({
                    po: location.state.lineItemPo,
                    shipment: location.state.lineItemShipment,
                  })}`,
                  {
                    state: {
                      fromShipment: true,
                      shipmentId: shipment?.Shipment,
                    },
                  }
                );
              } else {
                navigate('/');
              }
            }}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">
              {isCreate ? 'Create Shipment' : `Shipment: ${shipment?.Shipment || 'Loading...'}`}
            </h1>
            {location.state?.lineItemPo && location.state?.lineItemShipment && (
              <div className="flex items-center mt-1">
                <span className="text-gray-600 mr-2">From Line Item:</span>
                <button
                  onClick={() => {
                    navigate(
                      `/line-item?${new URLSearchParams({
                        po: location.state.lineItemPo,
                        shipment: location.state.lineItemShipment,
                      })}`,
                      { state: location.state.lineItemReturnState || {} }
                    );
                  }}
                  className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-800 hover:bg-accent-200 transition-colors border border-accent-200 shadow-sm"
                >
                  {`${location.state.lineItemPo}`}
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center space-x-3">
          {!isCreate && canEditShipment() && (
            <button
              onClick={toggleEdit}
              className={`px-4 py-2 rounded-md text-sm font-medium flex items-center space-x-1 transition-colors ${
                isEditing
                  ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  : 'bg-accent-600 text-white hover:bg-accent-700'
              }`}
            >
              {isEditing ? (
                <>
                  <X size={16} />
                  <span>Cancel</span>
                </>
              ) : (
                <>
                  <Edit size={16} />
                  <span>Edit</span>
                </>
              )}
            </button>
          )}
          {(isEditing || isCreate) && (
            <button
              onClick={handleSave}
              disabled={isSaving || (isCreate && !formData.Shipment)}
              className={cn(
                "flex items-center px-4 py-2 rounded-md",
                isSaving || (isCreate && !formData.Shipment)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-accent-600 text-white hover:bg-accent-700'
              )}
            >
              {isSaving ? (
                <Loader2 size={16} className="mr-1.5 animate-spin" />
              ) : (
                <Save size={16} className="mr-1.5" />
              )}
              {isSaving ? 'Saving...' : (isCreate ? 'Create' : 'Save Changes')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-3 flex items-center">
          <AlertCircle size={16} className="text-red-500 mr-2" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {renderField('Shipment', shipment?.Shipment, 'Shipment', null, true)}
            {renderField('Forwarder Name', shipment?.['Forwarder Name'], 'Forwarder Name', null, true)}
            {isEditing || isCreate ? (
              renderField('Line Items', shipment?.['Order Ref'], 'Order Ref', null, true)
            ) : (
              shipment?.['Order Ref'] && renderOrderRefs(shipment['Order Ref'])
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {renderField('Consignee Name', shipment?.['Consignee Name'], 'Consignee Name', null, true)}
            {renderField('Consignor Name', shipment?.['Consignor Name'], 'Consignor Name', null, true)}
            {renderField('Origin', shipment?.Origin, 'Origin', null, true)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {renderField('Destination', shipment?.['Dest.'], 'Dest.', null, true)}
            {renderField('House', shipment?.House, 'House', null, true)}
            {renderField('Master', shipment?.Master, 'Master', null, true)}
          </div>

          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex items-center',
                    activeTab === tab.id
                      ? 'border-accent-500 text-accent-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  )}
                >
                  {tab.icon}
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
          <div className="mt-6">
            {activeTab === 'dates' && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {renderDateField('OBL Received Date', shipment?.['OBL Received Date'], 'OBL Received Date', <Calendar size={16} />)}
                {renderDateField('Origin ETD', shipment?.['Origin ETD'], 'Origin ETD', <Calendar size={16} />)}
                {renderDateField('ATD', shipment?.ATD, 'ATD', <Calendar size={16} />)}
                {renderDateField('Dest. ETA', shipment?.['Dest. ETA'], 'Dest. ETA', <Calendar size={16} />)}
                {renderDateField('ATA', shipment?.ATA, 'ATA', <Calendar size={16} />)}
                {renderDateField('Est Delivery', shipment?.['Est Delivery'], 'Est Delivery', <Calendar size={16} />)}
                {renderDateField('Act Delivery', shipment?.['Act Delivery'], 'Act Delivery', <Calendar size={16} />)}
              </div>
            )}
            {activeTab === 'container' && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                {renderField('Charge', shipment?.['Charge'] || '-', 'Charge', <DollarSign size={16} />)}
                {renderField('Outer', shipment?.Outer || '-', 'Outer', <Package size={16} />)}
                {renderField('Volume', shipment?.Volume || '-', 'Volume', <Package size={16} />)}
                {renderField('Weight', shipment?.Weight || '-', 'Weight', <Package size={16} />)}
                
                {renderField('UQ - Charge', shipment?.['UQ-Charge'] || '-', 'UQ-Charge', <DollarSign size={16} />, true)}
                {renderField('UQ - Outer', shipment?.['UQ-Outer'] || '-', 'UQ-Outer', <Package size={16} />, true)}
                {renderField('UQ - Volume', shipment?.VOLUQ || '-', 'VOLUQ', <Package size={16} />, true)}
                {renderField('UQ - Weight', shipment?.WTUQ || '-', 'WTUQ', <Package size={16} />, true)}
                
                {renderField('Container #', shipment?.['Container #'] || '-', 'Container #', <Package size={16} />)}
                {renderField('Vessel Tracking', shipment?.Vessel || '-', 'Vessel', <Ship size={16} />)}
                {renderField('Trans', shipment?.Trans || '-', 'Trans', <Truck size={16} />, true)}
                {renderField('Mode', shipment?.Mode || '-', 'Mode', <Plane size={16} />, true)}
                
                {renderField('Cntr', shipment?.Cntr || '-', 'Cntr', <Package size={16} />)}
              </div>
            )}
            {activeTab === 'parts' && (
              <div>
                {renderLineItemsTable()}
              </div>
            )}
            {activeTab === 'documents' && (
              <div>
                  <ShipmentDocuments documents={documents} shipment={shipment.Shipment} forwarder={shipment?.['Forwarder Name']} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShipmentForm;
